import { GenericPaginatedListResponse, GenericResponse } from './main'
import { MbtiQuestionComparison } from './mbti-question'
import { HostInfo } from './quiz'
import { Profile } from './user'

export type PersonalityMajorType = 'SJ' | 'NF' | 'NT' | 'SP'

export enum MbtiProperty {
  E = 'E',
  N = 'N',
  T = 'T',
  J = 'J',
  I = 'I',
  S = 'S',
  F = 'F',
  P = 'P',
}

export enum MbtiType {
  E = '외향',
  N = '직관',
  T = '사고',
  J = '계획',
  I = '내향',
  S = '현실',
  F = '감정',
  P = '인식',
}

export enum MBTI {
  ENFP = 'ENFP',
  ENFJ = 'ENFJ',
  INFP = 'INFP',
  INFJ = 'INFJ',
  ESFP = 'ESFP',
  ESFJ = 'ESFJ',
  ISFP = 'ISFP',
  ISFJ = 'ISFJ',
  ENTP = 'ENTP',
  ENTJ = 'ENTJ',
  INTP = 'INTP',
  INTJ = 'INTJ',
  ESTP = 'ESTP',
  ESTJ = 'ESTJ',
  ISTP = 'ISTP',
  ISTJ = 'ISTJ',
}

export const MBTIList = Object.keys(MBTI)

export type MbtiRightType = 'I' | 'N' | 'F' | 'J'
export type MbtiLeftType = 'E' | 'S' | 'T' | 'P'

export interface MbtiDetail {
  type: MbtiType
  name: string
}

export interface MbtiResultInput {
  I: number
  N: number
  F: number
  J: number
  mbti: MBTI | undefined
}

export interface MbtiResult extends CalculatedResult {
  mbtiType: MBTI
}

export interface MbtiResultUserData {
  name: string
  mbti: MBTI
}

export interface MbtiHostQuizReport {
  id: number
  respondentFirstName: string
  mbtiType: MBTI
  totalCount: number
  profile: Profile | null
  isRemoved: boolean
}

export interface MyMbtiGuestQuizReport {
  id: number
  mbtiType: MBTI
  matchRate: number
  isRemoved: boolean
  profile: Profile | null // my profile
  hostQuizReport: {
    id: number
    isRemoved: boolean
    profile: Profile | null
    respondentFirstName: string
  }
}

export interface MyMbtiHostQuizReport extends MbtiHostQuizReport {
  createdAt: string
}

export interface MyMbtiHostQuizReportResponse extends GenericResponse {
  data: MyMbtiHostQuizReport
}

export interface MyMbtiHostQuizReportPaginatedListResponse extends GenericPaginatedListResponse {
  data: MyMbtiHostQuizReport[]
}

export interface MyMbtiGuestQuizReportPaginatedListResponse extends GenericPaginatedListResponse {
  data: MyMbtiGuestQuizReport[]
}

export interface MyMbtiHostQuizReportDetailResponse extends GenericResponse {
  data: MyMbtiHostQuizReportDetail
}

export interface MyMbtiHostQuizReportSummary {
  totalCount: number
  answerTypes?: {
    ranking: number
    mbtiType: MBTI
    proportion: number
  }[]
  closestAnswerReport?: ClosestAnswerReport
}

export interface MyMbtiHostQuizReportDetail extends MbtiQuizReportDetail {
  isOwner: boolean
  summary?: MyMbtiHostQuizReportSummary
}

export interface MbtiQuizReportDetail extends TypeSummary {
  id: number
  respondentFirstName: string
  respondentLastName: string
  profile: Profile | null
  createdAt?: string
  isProfileHidden: boolean
  isRemoved: boolean
}

// 임시 mbti 계산 결과
export interface TypeSummaryResponse extends GenericResponse {
  data: TypeSummary
}

export interface TypeSummary extends CalculatedResult {
  mbtiType: MBTI
}

export interface CalculatedResultResponse extends GenericResponse {
  data: CalculatedResult
}

export interface CalculatedResult {
  degreeI: number
  degreeN: number
  degreeF: number
  degreeJ: number
}

export interface MbtiGuestQuizReportDetailResponse extends GenericResponse {
  data: MbtiGuestQuizReportDetail
}

export interface MbtiGuestQuizReportDetail extends MbtiQuizReportDetail {
  hostQuizReport: Omit<MbtiQuizReportDetail, 'createdAt'>
  matchRate: number
  comparison: MbtiQuestionComparison[]
  isOwner: boolean
}

export interface MbtiHostInfo {
  id: number
  isProfileHidden: boolean
  profile: Profile
  respondentFirstName: string
}

export interface MbtiHostInfoResponse extends GenericResponse {
  data: Omit<HostInfo, 'answers' | 'questionGroup'>
}

/**
 * MBTI 이상형 관련
 */
export interface MbtiIdealTypeQuizReportDetail extends MbtiQuizReportDetail {
  totalMatchCount: number
  myMbti?: MbtiResult | null
  mbtiCharacteristic: Characteristic
  idealTypeCharacteristic: Characteristic
  matchRate?: number
  myMbtiMatchRate?: number
}

export interface MbtiIdealTypeQuizReportDetailResponse extends GenericResponse {
  data: MbtiIdealTypeQuizReportDetail
}

export interface MbtiIdealTypeMatch {
  id: number
  mbtiQuiz: {
    profile: Profile | null
    mbtiType: MBTI
    isProfileHidden: boolean
    respondentFirstName: string
  }
  matchRate: number
  respondentFirstName: string
}

export interface MbtiIdealTypeMatchPaginatedListResponse extends GenericPaginatedListResponse {
  data: MbtiIdealTypeMatch[]
}

export interface MbtiIdealTypeMatchQuizData extends MbtiResult {
  profile: Profile
  respondentFirstName: string
  isRemoved: boolean
}

export interface MatchComparison {
  mbtiIdealTypeQuizTitle: string
  mbtiIdealTypeQuizAnswer: number
  mbtiQuizTitle: string
  mbtiQuizAnswer: number
  ordering: string
  exampleLeft: string
  exampleRight: string
}

export interface MbtiIdealTypeMatchDetail {
  id: number
  mbtiIdealTypeQuiz: MbtiIdealTypeMatchQuizData
  mbtiQuiz: MbtiIdealTypeMatchQuizData
  matchRate: number
  ranking: number
  ownerType: 'mbti_ideal_type_quiz' | 'mbti_quiz'
  comparison: MatchComparison[]
  mbtiCharacteristic: Characteristic
  idealTypeCharacteristic: Characteristic
  respondentFirstName: string
  isProfileHidden: boolean
}

export interface MbtiIdealTypeMatchDetailResponse extends GenericResponse {
  data: MbtiIdealTypeMatchDetail
}

export interface MbtiIdealTypeMatchCheckResponse extends GenericResponse {
  data: {
    mbtiIdealTypeMatchId: number | null
  }
}

/**
 * MBTI 친친 관련
 */
export interface MbtiBestFriendQuizReportDetail extends MbtiQuizReportDetail {
  totalMatchCount: number
  myMbti?: MbtiResult | null
}

export interface MbtiBestFriendQuizReportDetailResponse extends GenericResponse {
  data: MbtiBestFriendQuizReportDetail
}

export interface MbtiBestFriendMatch {
  id: number
  mbtiQuiz: {
    profile: Profile | null
    mbtiType: MBTI
    isProfileHidden: boolean
    respondentFirstName: string
  }
  matchRate: number
}

export interface MbtiBestFriendMatchPaginatedListResponse extends GenericPaginatedListResponse {
  data: MbtiBestFriendMatch[]
}

export interface MbtiBestFriendMatchQuizData extends MbtiResult {
  profile: Profile
  respondentFirstName: string
}

export interface MatchComparison {
  mbtiBestFriendQuizTitle: string
  mbtiBestFriendQuizAnswer: number
  mbtiQuizTitle: string
  mbtiQuizAnswer: number
  ordering: string
  exampleLeft: string
  exampleRight: string
}

export interface MbtiBestFriendMatchDetail {
  id: number
  mbtiBestFriendQuiz: MbtiBestFriendMatchQuizData
  mbtiQuiz: MbtiBestFriendMatchQuizData
  matchRate: number
  ranking: number
  comparison: MatchComparison[]
}

export interface MbtiBestFriendMatchDetailResponse extends GenericResponse {
  data: MbtiBestFriendMatchDetail
}

export interface MbtiBestFriendMatchCheckResponse extends GenericResponse {
  data: {
    mbtiBestFriendMatchId: number | null
  }
}

export interface IdealTypeQuizReportSummary {
  totalCount: number
  answerTypes?: {
    ranking: number
    mbtiType: MBTI
    proportion: number
  }[]
  closestAnswerReport?: ClosestAnswerReport
}

/**
 * MBTI 이상형 퀴즈 관련
 */
export interface IdealTypeQuizReportDetail extends MyMbtiHostQuizReportDetail {
  createdAt: string
  myMbti?: MbtiResult
  totalReplyCount: number
  summary: IdealTypeQuizReportSummary
  myMbtiMatchRate?: number
  mbtiCharacteristic: Characteristic
  idealTypeCharacteristic: Characteristic
  comparison?: IdealTypeQuizMatchComparison[]
}

export interface Characteristic {
  datingStyles: string[]
  maleImageUrl: string
  femaleImageUrl: string
  likes: string[]
  dislikes: string[]
  title: string
}

export interface ClosestAnswerReport {
  id?: number
  respondentFirstName?: string
  respondentLastName?: string
  mbtiType?: MBTI
  matchRate?: number
  profile?: Profile | null
}

export interface IdealTypeRank {
  id: number
  profile?: Profile | null
  mbtiType: MBTI
  isProfileHidden: boolean
  respondentFirstName: string
  matchRate: number
}

export interface IdealTypeQuizMatchComparison {
  exampleLeft: string
  exampleRight: string
  guestAnswer: number
  hostAnswer: number
  ordering: number
  titleGuest: string
  titleHost: string
}

export interface CharacteristicImage {
  type: 'female' | 'male'
  imageUrl: string
}

export interface IdealTypeRankPaginatedListResponse extends GenericPaginatedListResponse {
  data: IdealTypeRank[]
}

export interface IdealTypeQuizReportDetailResponse extends GenericResponse {
  data: IdealTypeQuizReportDetail
}

export type IdealTypeGuestQuizReportDetail = MbtiGuestQuizReportDetail & {
  comparison: IdealTypeQuizMatchComparison[]
  characteristic: Characteristic
  ranking: number
  hostQuizReport: Omit<MbtiQuizReportDetail, 'createdAt'> & {
    characteristic: Characteristic
  }
}
export interface IdealTypeGuestQuizReportDetailResponse extends GenericResponse {
  data: IdealTypeGuestQuizReportDetail
}
